import { useState } from "react";
import { Eye, Pencil, Plus } from "lucide-react";
import { PatientV2CircularLoader } from "../shared/PatientV2CircularLoader";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { useGetData } from "@/hooks/useGetData";
import AxiosInstance from "@/utils/AxiosInstance";
import { formatDateTime12, getPromise, getPromiseToast } from "@/utils/helpers";
import { toast } from "sonner";

type SpecialInstruction = {
  _id?: string;
  instruction?: string;
  addedBy?: {
    _id?: string;
    name?: string;
    email?: string;
  };
  addedAt?: string;
};

type Props = {
  patientId: string;
  canManage?: boolean;
};

export function SpecialInstructionsSection({ patientId, canManage = false }: Props) {
  const [specialInstructions, setSpecialInstructions] = useState<SpecialInstruction>({});
  const [instructionDraft, setInstructionDraft] = useState("");
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isViewDialogOpen, setIsViewDialogOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const instructionText = specialInstructions.instruction?.trim() ?? "";
  const isLongInstruction = instructionText.length > 160;

  const {
    loading: specialInstructionLoading,
    err: specialInstructionErr,
  } = useGetData(
    `/patients/special-instructions/${patientId}`,
    {},
    false,
    !!patientId,
    (err, data) => {
      if (data && !err) {
        const next = (data as { data?: { specialInstructions?: SpecialInstruction } })?.data
          ?.specialInstructions;
        setSpecialInstructions(next || {});
        setInstructionDraft(next?.instruction || "");
      }
    },
  );

  const openDialog = () => {
    setInstructionDraft(specialInstructions.instruction || "");
    setIsDialogOpen(true);
  };

  const handleSave = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!patientId) {
      toast.error("Missing patient id");
      return;
    }

    const { pr, rejectFn, resolveFn } = getPromise();
    getPromiseToast(pr);

    try {
      setIsSaving(true);
      const { data } = await AxiosInstance().post("/patients/special-instruction", {
        patientId,
        instruction: instructionDraft.trim(),
      });
      const saved = (data as { data?: { specialInstructions?: SpecialInstruction } | null })?.data
        ?.specialInstructions;
      setSpecialInstructions(saved || {});
      setInstructionDraft(saved?.instruction || "");
      setIsDialogOpen(false);
      resolveFn("Special instruction saved");
    } catch (error) {
      rejectFn(error);
      toast.error("Failed to save special instruction");
    } finally {
      setIsSaving(false);
    }
  };

  const addedByName = specialInstructions.addedBy?.name?.trim() || "Unknown";
  const addedAtLabel = specialInstructions.addedAt
    ? formatDateTime12(specialInstructions.addedAt)
    : "";

  return (
    <>
      <div>
        {specialInstructionErr ? (
          <p className="rounded-lg border border-destructive px-3 py-2 text-sm text-destructive">
            Failed to load special instructions.
          </p>
        ) : null}

        {specialInstructionLoading ? (
          <PatientV2CircularLoader size="sm" className="p-4" />
        ) : instructionText ? (
          <div className="flex gap-2  overflow-hidden rounded-lg border border-secondary/50 text-secondary">
            {canManage ? (
              <Button
                type="button"
                onClick={openDialog}
                size="icon"
                className="h-auto w-11 shrink-0 self-start rounded-none border-r border-b rounded-br-xl  border-secondary/50 bg-white py-3 text-secondary hover:bg-secondary/10"
                aria-label="Edit special instruction"
              >
                <Pencil className="h-4 w-4" />
              </Button>
            ) : null}
            <div className="min-w-0 flex-1 py-2.5 pr-3">
              <div className="flex items-center justify-between gap-2">
                <p className="text-sm font-semibold text-secondary">Special Instruction</p>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="h-7 w-7 shrink-0 text-secondary hover:bg-secondary/10"
                  onClick={() => setIsViewDialogOpen(true)}
                  aria-label="View full special instruction"
                >
                  <Eye className="h-4 w-4" />
                </Button>
              </div>
              <p
                className={[
                  "mt-1 text-sm leading-relaxed break-words whitespace-pre-wrap text-foreground",
                  isLongInstruction ? "line-clamp-3" : "",
                ]
                  .filter(Boolean)
                  .join(" ")}
              >
                {instructionText}
              </p>              <p className="mt-2 text-xs text-secondary">
                {addedByName}
                {addedAtLabel ? (
                  <>
                    <span className="mx-1">•</span>
                    {addedAtLabel}
                  </>
                ) : null}
              </p>
            </div>
          </div>
        ) : (
          <div className="flex items-center gap-2 overflow-hidden rounded-lg border border-secondary/50 pr-3 text-secondary">
            {canManage ? (
              <Button
                type="button"
                onClick={openDialog}
                size="lg"
                className="h-auto shrink-0 self-stretch rounded-none border-0 border-r border-secondary/50 bg-white px-3 text-secondary hover:bg-secondary/10"
                aria-label="Add special instruction"
              >
                <Plus className="h-5 w-5" />
              </Button>
            ) : null}
            <p className="py-2.5 text-sm text-secondary">
              No special instructions have been added yet.
            </p>
          </div>
        )}
      </div>

      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {specialInstructions.instruction ? "Update" : "Add"} Special Instruction
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={(event) => void handleSave(event)} className="space-y-4 mt-4">
            <div className="space-y-2">
              {/* <Label htmlFor="special-instruction-content">Instruction</Label> */}
              <Textarea
                id="special-instruction-content"
                placeholder="Write any special instruction you want to share with the patient..."
                rows={6}
                value={instructionDraft}
                onChange={(event) => setInstructionDraft(event.target.value)}
              />
            </div>
            <div className="flex justify-end gap-3">
              <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={isSaving}>
                Save Instruction
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={isViewDialogOpen} onOpenChange={setIsViewDialogOpen}>
        <DialogContent className="max-h-[85vh] max-w-lg overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Special Instruction</DialogTitle>
          </DialogHeader>
          <p className="text-sm leading-relaxed break-words whitespace-pre-wrap text-foreground">
            {instructionText}
          </p>
          <p className="text-xs text-secondary">
            {addedByName}
            {addedAtLabel ? (
              <>
                <span className="mx-1">•</span>
                {addedAtLabel}
              </>
            ) : null}
          </p>
        </DialogContent>
      </Dialog>
    </>
  );
}
